import { Injectable, inject } from "@angular/core";

import { DISMISSED_NOTIFICATIONS_STORAGE_KEY } from "@core/constants/core.constants";
import { ENotificationId } from "@core/enums/notification.enum";
import { WINDOW } from "@core/tokens/window.token";

import { PlatformService } from "./platform.service";

type DismissedRecord = Record<string, ENotificationId[]>;

@Injectable({ providedIn: "root" })
export class NotificationDismissService {
    readonly #platform: PlatformService = inject(PlatformService);
    readonly #windowRef: Window = inject(WINDOW);

    public isDismissed(userId: number, notificationId: ENotificationId): boolean {
        const dismissed: ENotificationId[] = this.#read()[String(userId)] ?? [];

        return dismissed.includes(notificationId);
    }

    public dismiss(userId: number, notificationId: ENotificationId): void {
        if (!this.#platform.isPlatformBrowser) {
            return;
        }

        const data: DismissedRecord = this.#read();
        const key: string = String(userId);
        const current: ENotificationId[] = data[key] ?? [];

        if (current.includes(notificationId)) {
            return;
        }

        data[key] = [...current, notificationId];
        this.#windowRef.localStorage.setItem(
            DISMISSED_NOTIFICATIONS_STORAGE_KEY,
            JSON.stringify(data)
        );
    }

    #read(): DismissedRecord {
        if (!this.#platform.isPlatformBrowser) {
            return {};
        }

        const raw: string | null = this.#windowRef.localStorage.getItem(
            DISMISSED_NOTIFICATIONS_STORAGE_KEY
        );

        if (!raw) {
            return {};
        }

        try {
            const parsed: DismissedRecord = JSON.parse(raw);

            if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
                return parsed;
            }

            return {};
        } catch {
            return {};
        }
    }
}
